import * as React from "react";
import classNames from "classnames";
import {
  Select as UISelect,
  SelectProps as UISelectProps,
} from "@stemscopes/ui";
import uniqueId from "lodash-es/uniqueId";
import { useFormikContext } from "formik";
import InputError from "../input-error";
import { Spinner, Icon } from "../..";
import Tag from "../../tag";
import { formHooks } from "../../form/duck";
import styles from "./Select.module.css";
import { VirtualizedList } from "./components";

const Select = <TData extends any = any[]>({
  className,
  listContainerClassName,
  labelClassName,
  inputClassName,
  inputContainerClassName,
  labelPosition = "top",
  renderTag,
  name,
  stretch = true,
  disabled,
  icon,
  onBlur,
  hasVirtualization = false,
  ...rest
}: Partial<UISelectProps<TData>> & {
  hasVirtualization?: boolean;
}): React.ReactElement<any> => {
  const [field, { error: formikError }] = formHooks.useField<TData>({
    name,
  });
  const state = useFormikContext();
  const errorId = React.useMemo(() => uniqueId("error-"), []);
  // Formik can`t validate undefined value on subbmit
  const [error, setError] = React.useState(undefined);

  React.useEffect(() => {
    if (state?.isSubmitting || formikError) {
      setError((state.errors as any)[name || ""]);
    }
    if (Array.isArray(field.value) ? field.value.length : field.value) {
      setError(undefined);
    }
  }, [state?.isSubmitting, field.value, formikError]);

  return (
    <>
      {/* eslint-disable-next-line */}
      {/*
      // @ts-ignore */}
      <UISelect
        {...field}
        {...rest}
        disabled={disabled}
        stretch={stretch}
        labelPosition={labelPosition}
        icon={icon || <Icon name="down-outlined" size="small" />}
        className={classNames(styles.wrapper, className, {
          [styles.labelTop]: labelPosition === "top",
          [styles.labelLeft]: labelPosition === "left",
        })}
        labelClassName={classNames(styles.label, labelClassName)}
        inputClassName={classNames(styles.input, inputClassName, {
          [styles.error]: error,
        })}
        listContainerClassName={classNames(styles.list, listContainerClassName)}
        inputContainerClassName={classNames(styles.inputContainer, {
          [styles.disabled]: disabled,
          [styles.error]: rest.multi && error,
        })}
        renderTag={renderTag || Tag}
        spinner={<Spinner size="small" className={styles.spinner} />}
        aria-invalid={!!error}
        aria-errormessage={errorId}
        onBlur={
          onBlur ||
          (() => {
            // Formik can`t make field "touched: true" when props "multi" is false, for this we need to use field.onBlur
            if (field.onBlur && field.name && !rest.multi) {
              field.onBlur({
                target: { value: field?.value, name: field?.name },
              });
            }
          })
        }
      >
        {hasVirtualization ? (
          <VirtualizedList data={rest.data} />
        ) : (
          rest.children
        )}
      </UISelect>
      <InputError error={error} errorId={errorId} />
    </>
  );
};

export default Select;
